import { useMemo, useState } from "react";
import { CheckCheck, X } from "lucide-react";
import {
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Text,
} from "@musa.tech/design-system";
import { FilterDropdownButton } from "./FilterDropdownButton";
import { filterOptions } from "./filterOptionSearch";

export interface FilterOption {
  value: string;
  label: string;
}

interface OptionsFilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  /** Mostra os atalhos "Selecionar todos" / "Limpar seleção" no topo da lista. */
  showSelectAll?: boolean;
  /** `false` deixa o item marcado sozinho — usado onde a API aceita um valor só. */
  multiple?: boolean;
}

/**
 * Dropdown de múltipla escolha com busca. Serve Tipo de resíduo, Estado (UF),
 * Estabelecimento e Gerador.
 *
 * A busca é própria (`shouldFilter={false}`) para poder cortar a lista: há
 * geradores com milhares de estabelecimentos, e renderizar tudo travava o DOM.
 */
export const OptionsFilterDropdown = ({
  label,
  options,
  value,
  onChange,
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhuma opção disponível",
  isLoading = false,
  showSelectAll = false,
  multiple = true,
}: OptionsFilterDropdownProps) => {
  const [search, setSearch] = useState("");

  const { visible, total, truncated } = useMemo(
    () => filterOptions(options, search, value),
    [options, search, value],
  );

  const toggle = (optionValue: string) => {
    if (!multiple) {
      onChange(value.includes(optionValue) ? [] : [optionValue]);
      return;
    }
    onChange(
      value.includes(optionValue)
        ? value.filter((item) => item !== optionValue)
        : [...value, optionValue],
    );
  };

  // "Selecionar todos" ignora o corte de exibição (`visible`) — sempre marca
  // o total de `options`, igual ao padrão já usado no filtro de
  // Estabelecimento de outras telas (EstablishmentMultiSelect).
  const handleSelectAll = () => onChange(options.map((option) => option.value));
  const handleClearAll = () => onChange([]);
  const isAllSelected = options.length > 0 && value.length === options.length;

  return (
    <FilterDropdownButton
      label={label}
      count={value.length}
      isLoading={isLoading}
      disabled={!isLoading && options.length === 0}
      // Sem isso a próxima abertura herdaria o termo digitado antes.
      onOpenChange={(open) => !open && setSearch("")}
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={searchPlaceholder}
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>
            <Text variant="body" size="small" className="text-neutral-500">
              {options.length === 0 ? emptyMessage : "Nenhum resultado"}
            </Text>
          </CommandEmpty>
          {showSelectAll && !search.trim() && options.length > 1 && (
            <>
              <CommandGroup>
                {!isAllSelected && (
                  <CommandItem
                    value="__select-all__"
                    onSelect={handleSelectAll}
                    className="flex cursor-pointer items-center gap-2 text-primary"
                  >
                    <CheckCheck className="h-4 w-4" />
                    <Text variant="body" size="small">
                      Selecionar todos ({options.length})
                    </Text>
                  </CommandItem>
                )}
                {value.length > 0 && (
                  <CommandItem
                    value="__clear-all__"
                    onSelect={handleClearAll}
                    className="flex cursor-pointer items-center gap-2 text-destructive"
                  >
                    <X className="h-4 w-4" />
                    <Text variant="body" size="small">
                      Limpar seleção
                    </Text>
                  </CommandItem>
                )}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}
          <CommandGroup>
            {visible.map((option) => {
              const isSelected = value.includes(option.value);

              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => toggle(option.value)}
                  aria-label={`${option.label}${isSelected ? ", selecionado" : ""}`}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Checkbox
                    checked={isSelected}
                    tabIndex={-1}
                    aria-hidden="true"
                    className="pointer-events-none"
                  />
                  <Text variant="body" size="small" className="text-neutral-800">
                    {option.label}
                  </Text>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
        {truncated && (
          <div className="border-t border-neutral-200 px-3 py-2">
            <Text variant="body" size="xsmall" className="text-neutral-500">
              Mostrando {visible.length} de {total} — refine a busca
            </Text>
          </div>
        )}
      </Command>
    </FilterDropdownButton>
  );
};
